import { clients } from '@/lib/https';

/**
 * Calls a published Blocks Workflow webhook. The webhooks use `blocksAuthorization`, so the
 * caller's bearer token and x-blocks-key (added by `clients`) authorize the call. `url` is the full
 * webhook URL from the workflow's Webhook node (kept in a `VITE_*_WEBHOOK_URL` env var); `envName`
 * names that variable for the error thrown when it isn't configured.
 */
export const postWorkflowWebhook = async (
  url: string,
  envName: string,
  body: Record<string, unknown> = {}
): Promise<unknown> => {
  if (!url) throw new Error(`${envName} is not configured`);
  return clients.post(url, JSON.stringify(body));
};

/**
 * A webhook answering with `httpResponseData: "all"` wraps the last node's output in envelopes
 * (`{ executionId, status, data: [...] }`, item arrays, `json` / `output` fields), so search the
 * response breadth-first for the first object carrying `markerKey` instead of depending on one
 * envelope shape.
 */
export const findWorkflowOutput = <T>(response: unknown, markerKey: string): T | undefined => {
  const queue: unknown[] = [response];
  while (queue.length) {
    const value = queue.shift();
    if (Array.isArray(value)) queue.push(...value);
    else if (value && typeof value === 'object') {
      if (markerKey in value) return value as T;
      queue.push(...Object.values(value));
    }
  }
  return undefined;
};

/** The one envelope every Blocks workflow answers with (`.claude/skills/blocks-workflow/response-format`). */
export interface WorkflowResponse<TData = unknown> {
  IsSuccessful: boolean;
  /** Short sentence for the user; never empty on a failure. */
  Message: string;
  /** `""` on success, otherwise a code such as `VALIDATION_FAILED`, `CONFLICT` or `BUSINESS_RULE`. */
  ErrorCode: string;
  /** One entry per invalid field when `ErrorCode` is `VALIDATION_FAILED`, otherwise `[]`. */
  Errors: { Field: string; Message: string }[];
  /** `ItemId` of the record created or changed, else `""`. */
  EntityId: string;
  Data: TData | null;
}

/**
 * Reads a workflow's answer. A refusal is a normal `IsSuccessful: false` result, not an exception; an
 * answer with no envelope at all (the workflow did not run to its end) throws `failureCode`.
 */
export const readWorkflowResponse = <TData = unknown>(
  response: unknown,
  failureCode: string
): WorkflowResponse<TData> => {
  const result = findWorkflowOutput<WorkflowResponse<TData>>(response, 'IsSuccessful');
  if (!result) throw new Error(failureCode);
  return result;
};

/** A workflow's refusal; `message` is its translation key (`Data.MessageKey`) or, for invalid input,
 * the workflow's English message. The global error toast shows it translated. */
export class WorkflowRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkflowRefusedError';
  }
}

/**
 * Runs a workflow whose refusal should fail the mutation: a refusal throws `WorkflowRefusedError`, an
 * answer without an envelope throws `failureCode`; on success the envelope is returned.
 */
export const runWorkflow = async <TData = unknown>(
  url: string,
  envName: string,
  body: Record<string, unknown>,
  failureCode: string
): Promise<WorkflowResponse<TData & { MessageKey?: string }>> => {
  const result = readWorkflowResponse<TData & { MessageKey?: string }>(
    await postWorkflowWebhook(url, envName, body),
    failureCode
  );
  if (!result.IsSuccessful)
    throw new WorkflowRefusedError(result.Data?.MessageKey || result.Message);
  return result;
};
